import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import gibletLogo from '@/assets/giblet-logo.png';

export default function DemoCallForDestructionBounty() {
  const navigate = useNavigate();
  const location = useLocation();

  const [hasBounty, setHasBounty] = useState('yes');
  const [bountyAmount, setBountyAmount] = useState('2500');

  const amount = Number(bountyAmount) || 0;
  const gibletShare = amount * 0.01;
  const communityShare = amount * 0.99;

  const handleNext = () => {
    navigate('/demo-call-for-destruction/details', {
      state: {
        ...location.state,
        hasBounty: hasBounty === 'yes',
        bountyAmount: hasBounty === 'yes' ? amount : 0,
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Destruction Bounty</h1>
          <p className="text-lg text-muted-foreground">
            Configure the bounty and response window for this CfD
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-6">
          <RadioGroup value={hasBounty} onValueChange={setHasBounty}>
            <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="yes" id="bounty-yes" />
              <Label htmlFor="bounty-yes" className="cursor-pointer">
                <div className="font-semibold text-foreground">Yes, attach a Destruction Bounty</div>
                <div className="text-sm text-muted-foreground">Incentivize thorough review and resolution</div>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="no" id="bounty-no" />
              <Label htmlFor="bounty-no" className="cursor-pointer">
                <div className="font-semibold text-foreground">No bounty — standard review process</div>
              </Label>
            </div>
          </RadioGroup>

          {hasBounty === 'yes' && (
            <>
              <div className="space-y-2 border-t border-border pt-4">
                <Label>Bounty Amount (USD)</Label>
                <Input type="number" value={bountyAmount} onChange={(e) => setBountyAmount(e.target.value)} />
              </div>

              <div className="bg-destructive/10 rounded-lg p-4 space-y-2 text-sm">
                <h3 className="font-semibold text-foreground">Token Forfeiture Distribution</h3>
                <p className="text-muted-foreground">If the CfD is upheld and the KB is destroyed or modified:</p>
                <div className="flex justify-between pt-1">
                  <span className="text-muted-foreground">Giblet (Arbiter) — 1%:</span>
                  <span className="font-medium text-foreground">${gibletShare.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Composer Community — 99%:</span>
                  <span className="font-medium text-foreground">${communityShare.toLocaleString()}</span>
                </div>
              </div>
            </>
          )}

          <div className="border-t border-border pt-4 space-y-3">
            <h3 className="font-semibold text-foreground">Response Window</h3>
            <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Response deadline:</span>
                <span className="font-medium text-foreground">7 days from submission</span>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                The KB owner has 7 days to respond to the challenge. If no response is received,
                the CfD proceeds to Arbiter review.
              </p>
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <h3 className="font-semibold text-foreground">Review Team</h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="font-medium text-foreground">Reviewer</div>
                <div className="text-xs text-muted-foreground">You (challenger)</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="font-medium text-foreground">Respondent</div>
                <div className="text-xs text-muted-foreground">KB Owner</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="font-medium text-foreground">Arbiter</div>
                <div className="text-xs text-muted-foreground">Giblet (default)</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-destruction/evidence')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={handleNext} disabled={hasBounty === 'yes' && !bountyAmount} className="min-w-[120px]">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
